/**
 * Workshop showcase for ConversationEmbed — inline frame embeds in threads.
 *
 * Two mock conversations (Calendar "Felix — Week 43" with agenda + event
 * embeds; Gmail thread with draft + attachment embeds), plus edge cases
 * (streaming message, all-collapsed, all-expanded). Expand clicks only
 * report into a local log — they execute nothing (expand
 * simulates presentArtifactBesideConversation).
 *
 * Bubble recipe copied from conversation-frame-thread.tsx:355 — user:
 * max-w-[84%] accent-tinted bubble, right; assistant: bare text, left.
 * Deviation: assistant turns carrying embed cards render inside a dark
 * bg-elevated/60 bubble per the reference screenshots.
 *
 * NOTE: route registration in workshop-routes.tsx and sync-manifest.yaml
 * is owned by the main agent; this file only exports the page component.
 */

import { useState } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { ConversationEmbed } from "@/workshop/components/shapes/conversation-embed/conversation-embed";

// ─── Mock data (top of file) ─────────────────────────────────────────────────

const CALENDAR_SUGGESTIONS = ["Move lunch later", "Prep for the keynote", "Find a gap Friday"];

const GMAIL_SUGGESTIONS = ["Send the draft", "Attach the invoice", "Snooze to Monday"];

const EDGE_SUGGESTIONS = ["Summarize this thread", "Open all frames", "Mark resolved"];

// ─── Local thread primitives ─────────────────────────────────────────────────

function ThreadPanel({
  testId,
  appIcon,
  title,
  subtitle,
  statusPill,
  statusTone,
  children,
}: {
  testId: string;
  appIcon: React.ReactNode;
  title: string;
  subtitle: string;
  statusPill: string;
  statusTone: "success" | "neutral";
  children: React.ReactNode;
}) {
  return (
    <div
      className="mx-auto w-full max-w-[720px] overflow-hidden rounded-frame border border-glass-stroke bg-elevated/30"
      data-testid={testId}
    >
      <div
        className="flex items-center gap-2.5 border-b border-glass-stroke-faint px-4 py-3"
        data-testid={`${testId}-header`}
      >
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-control bg-elevated/60"
          aria-hidden="true"
        >
          {appIcon}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-canvas-sm font-semibold text-primary">
            {title}
          </p>
          <p className="truncate text-canvas-xs text-muted">{subtitle}</p>
        </div>
        <span
          className={cn(
            "shrink-0 rounded-pill px-2 py-0.5 text-canvas-xs font-medium",
            statusTone === "success"
              ? "bg-success-muted text-success"
              : "bg-elevated/60 text-secondary",
          )}
          data-testid={`${testId}-status-pill`}
        >
          {statusPill}
        </span>
      </div>
      <div className="flex flex-col gap-3 px-4 py-4" data-testid={`${testId}-thread`}>
        {children}
      </div>
    </div>
  );
}

function UserBubble({ testId, text }: { testId: string; text: string }) {
  return (
    <div className="flex w-full justify-end" data-testid={testId}>
      <div className="max-w-[84%] whitespace-pre-wrap rounded-preview bg-accent/18 px-3 py-2 text-canvas-base leading-relaxed text-primary">
        {text}
      </div>
    </div>
  );
}

function AssistantTurn({
  testId,
  text,
  children,
}: {
  testId: string;
  text: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex w-full justify-start" data-testid={testId}>
      <div className="flex w-full max-w-[96%] flex-col gap-2 rounded-preview bg-elevated/60 px-3 py-2.5">
        <p className="text-canvas-base leading-relaxed text-secondary">{text}</p>
        {children}
      </div>
    </div>
  );
}

function StreamingRow({ testId, text }: { testId: string; text: string }) {
  return (
    <div className="flex w-full justify-start" data-testid={testId}>
      <p className="max-w-[92%] text-canvas-base text-secondary">
        {text}
        <span
          className="ml-1 inline-block h-3.5 w-0.5 animate-pulse rounded-sm bg-accent align-middle"
          data-testid={`${testId}-caret`}
          aria-label="Streaming"
        />
      </p>
    </div>
  );
}

function ComposerMock({
  testId,
  suggestions,
}: {
  testId: string;
  suggestions: string[];
}) {
  const [draft, setDraft] = useState("");
  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-suggestions`}>
        {suggestions.map((chip) => (
          <button
            key={chip}
            type="button"
            onClick={() => setDraft(chip)}
            data-testid={`${testId}-suggestion-${chip.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={cn(
              "inline-flex items-center gap-1 rounded-pill px-2 py-1",
              "bg-white/[0.06] text-canvas-xs text-muted",
              "transition-colors hover:bg-white/[0.1] hover:text-secondary",
            )}
          >
            <Icons.CornerLeftUp className="h-2.5 w-2.5" aria-hidden="true" />
            {chip}
          </button>
        ))}
      </div>
      <div
        className="flex items-center gap-2 rounded-preview border border-glass-stroke bg-elevated/60 px-2 py-1.5"
        data-testid={`${testId}-bar`}
      >
        <button
          type="button"
          aria-label="Add attachment"
          data-testid={`${testId}-add-button`}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:text-secondary"
        >
          <Icons.Plus className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Reply in thread…"
          aria-label="Reply in thread"
          data-testid={`${testId}-input`}
          className="min-w-0 flex-1 bg-transparent text-canvas-sm text-primary outline-none placeholder:text-muted"
        />
        <button
          type="button"
          aria-label="Send reply"
          data-testid={`${testId}-send-button`}
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:text-secondary"
        >
          <Icons.Send className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

function Section({
  title,
  caption,
  testId,
  children,
}: {
  title: string;
  caption: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-10" data-testid={testId}>
      <h2 className="mb-1 text-sm font-semibold text-primary">{title}</h2>
      <p className="mb-4 text-canvas-sm text-muted">{caption}</p>
      <div className="flex flex-col gap-4">{children}</div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function ConversationEmbedsPage() {
  const [log, setLog] = useState<string[]>([]);

  const pushLog = (text: string) =>
    setLog((prev) => [text, ...prev].slice(0, 6));

  const handleExpand = (frameId: string) =>
    pushLog(`Opened ${frameId} beside conversation`);

  return (
    <div className="min-h-full bg-canvas p-8" data-testid="conversation-embeds-page">
      <div className="mb-8">
        <h1 className="text-lg font-bold text-primary">Conversation embeds</h1>
        <p className="mt-1 text-sm text-muted">
          Inline frame cards inside threads — collapsed preview, expand to the
          full frame. Clicks only land in the log.
        </p>
        <div
          className="mt-3 flex min-h-8 flex-wrap items-center gap-2"
          data-testid="conversation-embeds-interaction-log"
          aria-live="polite"
        >
          {log.length === 0 ? (
            <span className="text-xs text-muted">
              No interactions yet — expand a card.
            </span>
          ) : (
            log.map((text, i) => (
              <span
                key={`${text}-${i}`}
                className="rounded-pill bg-white/[0.08] px-2 py-1 text-xs text-secondary"
              >
                ↩ {text}
              </span>
            ))
          )}
        </div>
      </div>

      <Section
        title="Calendar — Felix, Week 43"
        caption="Agenda + event embeds with a resolved-conflict status."
        testId="conversation-embeds-calendar"
      >
        <ThreadPanel
          testId="conversation-embeds-calendar-thread"
          appIcon={<Icons.Calendar className="h-4 w-4 text-secondary" aria-hidden="true" />}
          title="Felix — Week 43"
          subtitle="Calendar · shared with Felix Brandt"
          statusPill="1 conflict resolved"
          statusTone="success"
        >
          <UserBubble
            testId="conversation-embeds-calendar-user-1"
            text="Can we still move lunch? The keynote runs into our slot."
          />
          <AssistantTurn
            testId="conversation-embeds-calendar-assistant-1"
            text="Moved lunch to 12:00 — here's your Wednesday. The keynote is the current block."
          >
            <ConversationEmbed
              frameId="agenda-w43"
              frameKind="agenda"
              title="Wednesday, Oct 22"
              subtitle="3 events · focus held"
              preview="Lunch 12:00 · Keynote 14:30 · Crit 16:00"
              onExpand={handleExpand}
            />
          </AssistantTurn>
          <AssistantTurn
            testId="conversation-embeds-calendar-assistant-2"
            text="Keynote details — main stage, 20 minutes. Expand for the full card."
          >
            <ConversationEmbed
              frameId="event-keynote"
              frameKind="event"
              title="Keynote — Designing with AI"
              subtitle="Wednesday · 14:30 – 14:50 · Config, Berlin"
              defaultExpanded
              onExpand={handleExpand}
            />
          </AssistantTurn>
          <ComposerMock
            testId="conversation-embeds-calendar-composer"
            suggestions={CALENDAR_SUGGESTIONS}
          />
        </ThreadPanel>
      </Section>

      <Section
        title="Gmail — Q4 invoice thread"
        caption="Draft + attachment embeds with waiting-threads status."
        testId="conversation-embeds-gmail"
      >
        <ThreadPanel
          testId="conversation-embeds-gmail-thread"
          appIcon={<Icons.Mail className="h-4 w-4 text-secondary" aria-hidden="true" />}
          title="Q4 invoice — Mara Jensen"
          subtitle="Gmail · 4 messages · last reply 2h ago"
          statusPill="3 threads waiting"
          statusTone="neutral"
        >
          <UserBubble
            testId="conversation-embeds-gmail-user-1"
            text="Draft a reply with the invoice attached. Keep it short."
          />
          <AssistantTurn
            testId="conversation-embeds-gmail-assistant-1"
            text="Drafted — short confirm plus the PDF. Review before I send anything."
          >
            <ConversationEmbed
              frameId="draft-invoice-reply"
              frameKind="draft"
              title="Re: Q4 invoice — confirm receipt"
              subtitle="Draft · to mara@dolcetto.example"
              preview="Thanks Mara — confirmed, payment lands Friday…"
              onExpand={handleExpand}
            />
            <ConversationEmbed
              frameId="attachment-invoice-pdf"
              frameKind="attachment"
              title="invoice-q4.pdf"
              subtitle="PDF · 2.4 MB · attached to draft"
              defaultExpanded
              onExpand={handleExpand}
            />
          </AssistantTurn>
          <ComposerMock
            testId="conversation-embeds-gmail-composer"
            suggestions={GMAIL_SUGGESTIONS}
          />
        </ThreadPanel>
      </Section>

      <Section
        title="Edge cases"
        caption="Streaming turn, all-collapsed thread, all-expanded thread, and a failed embed."
        testId="conversation-embeds-edge-cases"
      >
        <ThreadPanel
          testId="conversation-embeds-streaming-thread"
          appIcon={<Icons.Sparkles className="h-4 w-4 text-secondary" aria-hidden="true" />}
          title="Streaming turn"
          subtitle="Denker · answering now"
          statusPill="Live"
          statusTone="neutral"
        >
          <StreamingRow
            testId="conversation-embeds-streaming-message"
            text="Pulling your board snapshot — one moment"
          />
          <ConversationEmbed
            frameId="board-streaming"
            frameKind="board"
            title="Launch board"
            subtitle="Denker · Q4"
            status="streaming"
            onExpand={handleExpand}
          />
          <ComposerMock
            testId="conversation-embeds-streaming-composer"
            suggestions={EDGE_SUGGESTIONS}
          />
        </ThreadPanel>

        <ThreadPanel
          testId="conversation-embeds-collapsed-thread"
          appIcon={<Icons.Calendar className="h-4 w-4 text-secondary" aria-hidden="true" />}
          title="All collapsed"
          subtitle="Calendar · 3 embeds, none expanded"
          statusPill="3 threads waiting"
          statusTone="neutral"
        >
          <AssistantTurn
            testId="conversation-embeds-collapsed-assistant-1"
            text="Three cards, all collapsed — expand any of them."
          >
            <ConversationEmbed
              frameId="agenda-collapsed"
              frameKind="agenda"
              title="Thursday, Oct 23"
              subtitle="2 events"
              onExpand={handleExpand}
            />
            <ConversationEmbed
              frameId="event-collapsed"
              frameKind="event"
              title="Design systems crit"
              subtitle="Thursday · 16:00 · Studio call"
              onExpand={handleExpand}
            />
            <ConversationEmbed
              frameId="board-collapsed"
              frameKind="board"
              title="Launch board"
              subtitle="Denker · Q4"
              onExpand={handleExpand}
            />
          </AssistantTurn>
        </ThreadPanel>

        <ThreadPanel
          testId="conversation-embeds-expanded-thread"
          appIcon={<Icons.Mail className="h-4 w-4 text-secondary" aria-hidden="true" />}
          title="All expanded"
          subtitle="Gmail · every embed open inline"
          statusPill="1 conflict resolved"
          statusTone="success"
        >
          <AssistantTurn
            testId="conversation-embeds-expanded-assistant-1"
            text="Everything open — collapse anything you don't need."
          >
            <ConversationEmbed
              frameId="agenda-expanded"
              frameKind="agenda"
              title="Wednesday, Oct 22"
              subtitle="4 events · focus held"
              defaultExpanded
              onExpand={handleExpand}
            />
            <ConversationEmbed
              frameId="draft-expanded"
              frameKind="draft"
              title="Re: Q4 invoice — confirm receipt"
              subtitle="Draft · to mara@dolcetto.example"
              defaultExpanded
              onExpand={handleExpand}
            />
          </AssistantTurn>
        </ThreadPanel>

        <ThreadPanel
          testId="conversation-embeds-failed-thread"
          appIcon={<Icons.CircleAlert className="h-4 w-4 text-secondary" aria-hidden="true" />}
          title="Failed + untitled"
          subtitle="Denker · degraded states"
          statusPill="Needs attention"
          statusTone="neutral"
        >
          <AssistantTurn
            testId="conversation-embeds-failed-assistant-1"
            text="This card failed to load — and one arrived without a title."
          >
            <ConversationEmbed
              frameId="event-failed"
              frameKind="event"
              title="Keynote — Designing with AI"
              subtitle="Wednesday · 14:30 · Config, Berlin"
              status="error"
              onExpand={handleExpand}
            />
            <ConversationEmbed
              frameId="draft-untitled"
              frameKind="draft"
              title=""
              subtitle="Draft · autosaved 09:41"
              onExpand={handleExpand}
            />
          </AssistantTurn>
        </ThreadPanel>
      </Section>
    </div>
  );
}
